import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useProductLevelRequirementsProgress } from '../hooks/useProductLevelRequirementsProgress'
import { V2BriefingGuidanceHelpTrigger } from './V2BriefingGuidanceHelpTrigger'
import { V2RohsSharedEvidenceFileDialog } from './rohs-legislation-briefing/V2RohsSharedEvidenceFileDialog'
import { V2RohsSharedEvidenceFileItem } from './rohs-legislation-briefing/V2RohsSharedEvidenceFileItem'
import { V2ProductDescriptionEditDialog } from './rohs/V2ProductDescriptionEditDialog'
import { V2ProductModelEditDialog } from './V2ProductModelEditDialog'

type V2ProductDetailsCardProps = {
  productId: string
}

export function V2ProductDetailsCard({ productId }: V2ProductDetailsCardProps) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false)
  const [modelDialogOpen, setModelDialogOpen] = useState(false)
  const [identificationDialogOpen, setIdentificationDialogOpen] =
    useState(false)
  const {
    product,
    isLoading,
    hasProductDescription,
    hasProductIdentification,
    identificationFileName,
  } = useProductLevelRequirementsProgress(productId)

  if (isLoading) {
    return (
      <Card className='h-fit' aria-busy='true'>
        <CardHeader className='pb-3'>
          <Skeleton className='h-5 w-36' />
          <Skeleton className='mt-2 h-4 w-full max-w-sm' />
        </CardHeader>
        <CardContent className='space-y-6'>
          <Skeleton className='h-16 w-full' />
          <Skeleton className='h-16 w-full' />
        </CardContent>
      </Card>
    )
  }

  let descriptionText = t(
    'v2.pages.products.detail.rohsBriefing.productDescription.noDescription'
  )
  if (hasProductDescription && product?.description != null) {
    descriptionText = product.description
  }

  const modelText =
    product?.productModel != null && product.productModel.trim() !== ''
      ? product.productModel.trim()
      : null
  const showModelNotSet = modelText == null

  const showDescriptionEditButton = canWrite
  const showModelEditButton = canWrite
  const showIdentificationUploadButton = canWrite && !hasProductIdentification
  const hasIdentificationFile = identificationFileName != null
  const showIdentificationNoFile = !hasIdentificationFile

  return (
    <>
      <Card className='h-fit'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base leading-tight'>
            {t('v2.pages.products.detail.productDetails.title')}
          </CardTitle>
          <CardDescription className='text-xs'>
            {t('v2.pages.products.detail.productDetails.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <section className='space-y-2'>
            <div className='flex items-center justify-between gap-2'>
              <h3 className='text-sm font-medium'>
                {t('v2.pages.products.detail.productModel.title')}
              </h3>
              {showModelEditButton && (
                <Button
                  type='button'
                  size='xs'
                  variant='outline'
                  aria-label={t(
                    'v2.pages.products.detail.productModel.edit.title'
                  )}
                  onClick={() => {
                    setModelDialogOpen(true)
                  }}
                >
                  {t('v2.pages.products.detail.productModel.edit.action')}
                </Button>
              )}
            </div>
            {showModelNotSet && (
              <p className='text-sm text-muted-foreground'>
                {t('v2.pages.products.detail.productModel.notSet')}
              </p>
            )}
            {!showModelNotSet && modelText != null && (
              <p className='text-sm text-muted-foreground'>{modelText}</p>
            )}
          </section>

          <hr />

          <section className='space-y-2'>
            <div className='flex items-center justify-between gap-2'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <h3 className='text-sm font-medium'>
                  {t(
                    'v2.pages.products.detail.rohsBriefing.steps.items.productDescription.title'
                  )}
                </h3>
                <V2BriefingGuidanceHelpTrigger guidanceBase='v2.pages.products.detail.productDetails.guidance.productDescription' />
              </div>
              {showDescriptionEditButton && (
                <Button
                  type='button'
                  size='xs'
                  variant='outline'
                  onClick={() => {
                    setDescriptionDialogOpen(true)
                  }}
                >
                  {t(
                    'v2.pages.products.detail.rohsBriefing.productDescription.action'
                  )}
                </Button>
              )}
            </div>
            <p className='text-sm whitespace-pre-wrap text-muted-foreground'>
              {descriptionText}
            </p>
          </section>

          <hr />

          <section className='space-y-2'>
            <div className='flex items-start justify-between gap-2'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <h3 className='text-sm font-medium'>
                  {t(
                    'v2.pages.products.detail.rohsBriefing.steps.items.productIdentification.title'
                  )}
                </h3>
                <V2BriefingGuidanceHelpTrigger guidanceBase='v2.pages.products.detail.sharedEvidence.guidance.productIdentification' />
              </div>
              {showIdentificationUploadButton && (
                <Button
                  type='button'
                  variant='outline'
                  size='xs'
                  onClick={() => {
                    setIdentificationDialogOpen(true)
                  }}
                >
                  {t('v2.pages.products.detail.sharedEvidence.actions.upload')}
                </Button>
              )}
            </div>
            {hasIdentificationFile && identificationFileName != null && (
              <V2RohsSharedEvidenceFileItem
                fileName={identificationFileName}
                canWrite={canWrite}
                onEdit={() => {
                  setIdentificationDialogOpen(true)
                }}
              />
            )}
            {showIdentificationNoFile && (
              <p className='text-sm text-muted-foreground'>
                {t('v2.pages.products.detail.sharedEvidence.noFile')}
              </p>
            )}
          </section>
        </CardContent>
      </Card>

      <V2ProductDescriptionEditDialog
        productId={productId}
        description={product?.description}
        open={descriptionDialogOpen}
        onOpenChange={setDescriptionDialogOpen}
      />
      <V2ProductModelEditDialog
        productId={productId}
        productModel={product?.productModel}
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
      />
      <V2RohsSharedEvidenceFileDialog
        productId={productId}
        slotKind='PRODUCT_IDENTIFICATION'
        fileName={identificationFileName}
        open={identificationDialogOpen}
        onOpenChange={setIdentificationDialogOpen}
      />
    </>
  )
}
